import { getConfig } from '@config';
import React, { useEffect } from 'react';
import { ClarityLoaderScript } from './ClarityLoaderScript';
import { identifyWithClarity, upgradeClaritySession } from './identifyWithClarity';

type ClarityServiceProps = Readonly<{
  /**
   * Asks Clarity to keep this page load's recording. The project tag runs in lean mode: a session uploads playback
   * only once Clarity's own sampling or an `upgrade` promotes it, and otherwise is listed with no recording.
   * Pass it on surfaces where every session should be replayable.
   */
  upgradeReason?: string;
}>;

/**
 * https://clarity.microsoft.com/
 * Free heatmaps & session recordings
 *
 */
export const ClarityService = ({ upgradeReason }: ClarityServiceProps = {}) => {
  const isEnabled = getConfig().microsoftClarityEnabled;

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    // Queue-safe: when this effect beats Helmet's loader snippet to the frame, both calls queue on the same stub the
    // snippet keeps, and the tag replays the queue after `start`. Identify is once per page load, so a changed
    // `upgradeReason` re-running this effect only adds the upgrade.
    identifyWithClarity();

    if (upgradeReason) {
      // Retroactive to the start of the page load. Sent on mount, it also lands before lean mode's 10MB local
      // playback buffer can fill and drop frames.
      upgradeClaritySession(upgradeReason);
    }
  }, [isEnabled, upgradeReason]);

  if (!isEnabled) {
    return null;
  }

  return <ClarityLoaderScript projectId="hvn8o07eqd" />;
};
